import React, { useState } from "react";
import {
  Table,
  Segment,
  Button,
  Header,
  Message,
  Divider,
  Icon,
  Input,
} from "semantic-ui-react";
import dvdlocator from "../../../../../../apis/dvdlocator";

const CompletedStep = ({ supplyUnits, setSupplyUnits, onFinish }) => {
  const [editingUpc, setEditingUpc] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successUpc, setSuccessUpc] = useState(null);

  const unitsToProcess = supplyUnits.filter(
    (unit) => unit.assign_new_inventory
  );

  const groupedUnits = unitsToProcess.reduce((groups, unit) => {
    const groupId = unit.new_inventory_id;
    if (!groups[groupId]) {
      groups[groupId] = [];
    }
    groups[groupId].push(unit);
    return groups;
  }, {});

  const updateSupplyTitle = async (upc, newTitle) => {
    setIsLoading(true);
    setError(null);

    try {
      await dvdlocator.put(`/supply/${upc}`, { supply_title: newTitle });

      // Update the specific unit in supplyUnits by UPC
      const updatedUnits = supplyUnits.map((unit) => {
        if (unit.upc === upc) {
          return { ...unit, title: newTitle };
        }
        return unit;
      });

      // Update the state
      setSupplyUnits(updatedUnits);

      // Show success indicator briefly
      setSuccessUpc(upc);
      setTimeout(() => {
        setSuccessUpc(null);
      }, 2000);
    } catch (err) {
      console.error(err);
      setError("Failed to update title");
    } finally {
      setIsLoading(false);
      setEditingUpc(null);
    }
  };

  const handleEditClick = (upc, currentTitle) => {
    setEditingUpc(upc);
    setEditTitle(currentTitle);
  };

  const handleSaveTitle = (upc) => {
    if (editTitle.trim() !== "") {
      updateSupplyTitle(upc, editTitle);
    } else {
      setError("Title cannot be empty");
    }
  };

  const handleCancelEdit = () => {
    setEditingUpc(null);
    setError(null);
  };

  const remainingUnits = supplyUnits.filter(
    (unit) => !unit.assign_new_inventory
  );

  const calculateTotalInventory = (units) => {
    return units.reduce((sum, unit) => sum + (unit.inv || 0), 0);
  };

  return (
    <Segment>
      <Message positive icon>
        <Icon name="check circle" />
        <Message.Content>
          <Message.Header>Inventory Changes Completed</Message.Header>
          <p>All inventory changes have been successfully processed.</p>
        </Message.Content>
      </Message>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Header as="h3">Inventory Summary</Header>
      <p>Here's a summary of your inventory changes:</p>

      <Message info>
        <p>
          <strong>{Object.keys(groupedUnits).length}</strong> new inventory
          {Object.keys(groupedUnits).length === 1 ? " group " : " groups "}
          created with <strong>{unitsToProcess.length}</strong> total
          {unitsToProcess.length === 1 ? " item" : " items"}.
        </p>
      </Message>

      <Header as="h4">New Inventory Groups</Header>
      {Object.keys(groupedUnits).length === 0 ? (
        <Message info>
          <p>No items were assigned to new inventory groups.</p>
        </Message>
      ) : (
        Object.entries(groupedUnits).map(([groupId, units]) => {
          const groupTitle =
            units[0]?.new_inventory_title ||
            `Group ${Math.abs(parseInt(groupId))}`;
          const groupShelf = units[0]?.new_inventory_shelf || "";

          const totalInventory = calculateTotalInventory(units);

          return (
            <Segment key={groupId}>
              <Header as="h5">Group {Math.abs(parseInt(groupId))}</Header>
              <p>
                <strong>Title:</strong> {groupTitle}
              </p>
              <p>
                <strong>Shelf:</strong> {groupShelf}
              </p>
              <p>
                <strong>Total Inventory:</strong> {totalInventory}
              </p>
              <Table celled compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>UPC</Table.HeaderCell>
                    <Table.HeaderCell>Title</Table.HeaderCell>
                    <Table.HeaderCell>Inventory</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {units.map((unit, index) => (
                    <Table.Row key={`${unit.upc}-${index}`}>
                      <Table.Cell>{unit.upc}</Table.Cell>
                      <Table.Cell>
                        {editingUpc === unit.upc ? (
                          <Input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            error={error !== null}
                            action
                            fluid
                            size="small"
                          >
                            <input />
                            <Button
                              color="green"
                              icon="check"
                              size="tiny"
                              onClick={() => handleSaveTitle(unit.upc)}
                              loading={isLoading}
                              disabled={isLoading}
                            />
                            <Button
                              icon="cancel"
                              size="tiny"
                              onClick={handleCancelEdit}
                            />
                          </Input>
                        ) : (
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {unit.supply_title}
                            {successUpc === unit.upc && (
                              <Icon
                                name="check circle"
                                color="green"
                                style={{ marginLeft: "8px" }}
                              />
                            )}
                            {!successUpc && (
                              <Icon
                                name="edit"
                                size="small"
                                link
                                onClick={() =>
                                  handleEditClick(unit.upc, unit.supply_title)
                                }
                                style={{ marginLeft: "8px", cursor: "pointer" }}
                              />
                            )}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>{unit.inv || 0}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          );
        })
      )}

      <Divider />

      <Header as="h4">Remaining in Original Inventory</Header>
      {remainingUnits.length === 0 ? (
        <Message info>
          <p>All items have been moved to new inventory groups.</p>
        </Message>
      ) : (
        <Segment>
          <p>
            <strong>Total Items:</strong> {remainingUnits.length}
          </p>
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>UPC</Table.HeaderCell>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Inventory</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {remainingUnits.map((unit, index) => (
                <Table.Row key={`${unit.upc}-${index}`}>
                  <Table.Cell>{unit.upc}</Table.Cell>
                  <Table.Cell>{unit.supply_title}</Table.Cell>
                  <Table.Cell>{unit.inv || 0}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      )}

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button onClick={onFinish}>Finish</Button>
      </div>
    </Segment>
  );
};

export default CompletedStep;
